import React from 'react'
import ReactDOM from 'react-dom'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import App from './App'
import NotFound from 'routes/NotFound'
import reportWebVitals from './reportWebVitals'
import { store } from 'store/store'
import { AppMode } from 'helpers/types'
import pl from 'translations/pl.json'
import en from 'translations/en.json'
import uk from 'translations/uk.json'
import { BrowserRouter } from 'react-router-dom'
import { BaseCSS, GridThemeProvider } from 'styled-bootstrap-grid'
import { gridTheme } from './styles/theme'
import GlobalStyles from './styles/globalStyles'
import { ThemeProvider } from 'styled-components/macro'
import Page from './components/global/Page'
import {
    CreatorStepObject,
    STEPS,
    CreatorAccommodationStepObject,
    CREATOR_ACCOMMODATION_STEPS,
    SelfcareStepObject,
    SELFCARE_STEPS,
    UnlimitedCardStepObject,
    UNLIMITED_CARD_STEPS,
    UnlimitedRenewStepObject,
    UNLIMITED_RENEW_STEPS,
    VouchersStepObject,
    VOUCHERS_STEPS,
} from 'routes/steps'
import { TITLES } from 'translations/standalone/titles'
import { getThemeByLanguage } from 'helpers/theme'
import {
    ALLOWED_CREATOR_ACCOMMODATION_BASE_NAMES,
    ALLOWED_CREATOR_BASE_NAMES,
    ALLOWED_GLOBAL_BASE_NAMES,
    ALLOWED_SELFCARE_BASE_NAMES,
    ALLOWED_UNLIMITED_CARD_BASE_NAMES,
    ALLOWED_UNLIMITED_RENEW_BASE_NAMES,
    ALLOWED_VOUCHERS_BASE_NAMES,
} from 'constants/routes'
import { AVAILABLE_LANGUAGES, DEFAULT_LANG } from 'constants/i18n'

const { pathname } = window.location

if (pathname === '/' || ALLOWED_GLOBAL_BASE_NAMES.includes(pathname))
    window.history.replaceState(null, '', ALLOWED_CREATOR_BASE_NAMES[0])

const globalBasename = ALLOWED_GLOBAL_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const creatorBasename = ALLOWED_CREATOR_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const creatorAccommodationBasename =
    ALLOWED_CREATOR_ACCOMMODATION_BASE_NAMES.find((item) =>
        pathname.includes(item)
    )

const selfcareBasename = ALLOWED_SELFCARE_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const unlimitedCardBasename = ALLOWED_UNLIMITED_CARD_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const unlimitedRenewBasename = ALLOWED_UNLIMITED_RENEW_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const vouchersBasename = ALLOWED_VOUCHERS_BASE_NAMES.find((item) =>
    pathname.includes(item)
)

const checkAppBasename = () => {
    if (globalBasename) {
        return 'global'
    } else if (selfcareBasename) {
        return 'selfcare'
    } else if (vouchersBasename) {
        return 'vouchers'
    } else if (unlimitedRenewBasename) {
        return 'unlimited-renew'
    } else if (unlimitedCardBasename) {
        return 'unlimited-card'
    } else if (creatorAccommodationBasename) {
        return 'creator-accommodation'
    } else if (creatorBasename) {
        return 'creator'
    } else {
        return 'error'
    }
}

const appMode: AppMode = checkAppBasename()

let steps:
    | CreatorStepObject[]
    | CreatorAccommodationStepObject[]
    | SelfcareStepObject[]
    | UnlimitedCardStepObject[]
    | UnlimitedRenewStepObject[]
    | VouchersStepObject[] = STEPS

switch (appMode) {
    case 'creator-accommodation':
        steps = CREATOR_ACCOMMODATION_STEPS
        break
    case 'selfcare':
        steps = SELFCARE_STEPS
        break
    case 'unlimited-card':
        steps = UNLIMITED_CARD_STEPS
        break
    case 'unlimited-renew':
        steps = UNLIMITED_RENEW_STEPS
        break
    case 'vouchers':
        steps = VOUCHERS_STEPS
        break
}

export default steps

const renderCreator = async () => {
    const messages: Record<string, Record<string, string>> = {
        pl,
        en,
        uk,
    }

    const isBasenameAllowed = [
        ...ALLOWED_CREATOR_BASE_NAMES,
        ...ALLOWED_CREATOR_ACCOMMODATION_BASE_NAMES,
        ...ALLOWED_SELFCARE_BASE_NAMES,
        ...ALLOWED_UNLIMITED_CARD_BASE_NAMES,
        ...ALLOWED_UNLIMITED_RENEW_BASE_NAMES,
        ...ALLOWED_VOUCHERS_BASE_NAMES,
        ...ALLOWED_GLOBAL_BASE_NAMES,
    ].some((item) => pathname.includes(item))

    // TODO: rewrite detecting basename to not use 'include' - matching shorter urls break logic
    // ! dont change order of lines for now !
    const basename =
        vouchersBasename ||
        creatorAccommodationBasename ||
        creatorBasename ||
        selfcareBasename ||
        unlimitedCardBasename ||
        unlimitedRenewBasename ||
        globalBasename

    const getLanguageFromBasename = () => {
        const languageFromPath = basename?.split('/')[1]

        if (
            isBasenameAllowed &&
            languageFromPath &&
            AVAILABLE_LANGUAGES.includes(languageFromPath)
        )
            return languageFromPath
        return DEFAULT_LANG
    }

    const languageFromBasename = getLanguageFromBasename()

    document.documentElement.setAttribute('lang', languageFromBasename)
    document.title = TITLES[appMode]

    ReactDOM.render(
        <React.StrictMode>
            <IntlProvider
                messages={messages[languageFromBasename]}
                locale={languageFromBasename}
                defaultLocale={DEFAULT_LANG}
            >
                <Provider store={store}>
                    <ThemeProvider
                        theme={getThemeByLanguage(languageFromBasename)}
                    >
                        <GridThemeProvider gridTheme={gridTheme}>
                            <>
                                <BaseCSS />
                                <GlobalStyles />
                                {/* TODO: refactor to createBrowserRouter + loaders + etc. */}
                                <BrowserRouter basename={basename}>
                                    {isBasenameAllowed &&
                                    appMode !== 'error' ? (
                                        <App appMode={appMode} />
                                    ) : (
                                        <Page>
                                            <NotFound />
                                        </Page>
                                    )}
                                </BrowserRouter>
                            </>
                        </GridThemeProvider>
                    </ThemeProvider>
                </Provider>
            </IntlProvider>
        </React.StrictMode>,
        document.getElementById('root')
    )
}

renderCreator()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
